import { BannerSlidePanelProps } from '../../../interface/interface';
import CarouselButton from './CarouselButton';
import BannerCurveDecoration from './BannerCurveDecoration';

const BannerSlidePanel = ({
  slide,
  isActive,
  slideNumber,
  slideCount,
  panelId,
}: Readonly<BannerSlidePanelProps>) => {
  const { eyebrow, heading, description, button, image, theme } = slide;

  const imageStyle = image.objectPosition
    ? { objectPosition: image.objectPosition }
    : undefined;

  const isFirstSlide = slideNumber === 1;

  return (
    <div
      id={panelId}
      role="group"
      aria-roledescription="slide"
      aria-label={`${slideNumber} of ${slideCount}`}
      aria-hidden={!isActive}
      className="relative w-full flex-shrink-0 overflow-hidden"
      style={{ backgroundColor: theme?.backgroundColor ?? '#ffffff' }}
    >
      <BannerCurveDecoration />
      <div className="absolute inset-y-0 right-0 hidden w-1/2 md:block lg:w-[46%]">
        <picture>
          <img
            src={image.src}
            alt={image.alt}
            loading={isFirstSlide ? 'eager' : 'lazy'}
            fetchPriority={isFirstSlide ? 'high' : 'auto'}
            decoding="async"
            className="h-full w-full object-cover"
            style={imageStyle}
          />
        </picture>
      </div>

      <div className="container-standard relative z-10">
        <div
          className={`flex min-h-[480px] flex-col justify-center gap-6 py-10
            sm:min-h-[440px] md:min-h-[420px] md:gap-5 md:py-14
            lg:min-h-[480px] lg:py-16`}
        >
          <div className="flex flex-col gap-3 md:max-w-[48%] md:gap-4 lg:max-w-[44%]">
            {eyebrow && (
              <div className="flex items-center gap-2">
                {eyebrow.icon && (
                  <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-secondary-pink-50 sm:h-9 sm:w-9">
                    {eyebrow.icon}
                  </span>
                )}

                <span
                  className="text-xs font-semibold uppercase tracking-wide sm:text-sm"
                  style={{ color: theme?.eyebrowColor ?? '#d15f9b' }}
                >
                  {eyebrow.text}
                </span>
              </div>
            )}

            <h2 className="text-2xl font-bold leading-tight text-primary-blue-500 sm:text-3xl md:text-4xl lg:text-5xl">
              {heading}
            </h2>

            <p className="max-w-md text-sm text-grey-700 sm:text-base">
              {description}
            </p>

            <div className="mt-1 sm:mt-2">
              <CarouselButton button={button} />
            </div>
          </div>

          {/* Mobile: image stacks below the text */}
          <div className="relative aspect-[4/3] w-full overflow-hidden rounded-2xl md:hidden">
            <picture>
              {image.mobileSrc && (
                <source media="(max-width: 767px)" srcSet={image.mobileSrc} />
              )}

              <img
                src={image.src}
                alt={image.alt}
                loading={isFirstSlide ? 'eager' : 'lazy'}
                fetchPriority={isFirstSlide ? 'high' : 'auto'}
                decoding="async"
                className="h-full w-full object-cover"
                style={imageStyle}
              />
            </picture>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerSlidePanel;
